import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { AnimatePresence, motion } from "framer-motion";

import { Shot, getShotPlaybackSrc } from "../../../../app/services/shots";
import { WHEEL_GESTURE_SURFACE_ATTR } from "../../../../components/form-item/RadioCardGroup";
import DateDisplay, {
  DATE_DISPLAY_TYPE,
} from "../../../../components/date-display/DateDisplay";
import GenerativeAudioThumbnail from "../../../../components/media-preview/components/audio-thumbnail/GenerativeAudioThumbnail";
import { getFileIconFromExtension } from "../../../../components/file-icon/fileIcon";
import { shotHasMedia } from "../../../../constant/shotTableStatus";
import { WHEEL_SHOT_MEDIA_ATTR } from "../../../../utils/sharedMediaTransition";
import {
  hashString,
  mulberry32,
  CLOUD_STAGGER_SEC,
  CLOUD_BUBBLE_FADE_SEC,
  type CloudRect,
} from "../../../../utils/cloud-layout/commentCloudLayout";
import {
  AUDIO_EXTENSIONS,
  IMAGE_EXTENSIONS,
  VIDEO_EXTENSIONS,
  extOfUrl,
} from "./mediaFileExt";
import {
  layoutLabelClusterChips,
  type ClusterChipSpec,
} from "../../../../utils/cloud-layout/waitingCloudLayout";

/**
 * 待機中スロット（WaitingLabelCard）選択時に、中央のラベルを囲うように
 * 待機中ショットのサムネイルチップを雲状に散らして表示するオーバーレイ。
 *
 * ShotCommentCloud と同じ構成（body ポータル + settled 起点のスタガー
 * フェードイン）だが、次の点が異なる:
 *  - 配置はラベルを核にした放射状クラスタ（waitingCloudLayout）。カードの
 *    大きさに依存せず、広いビューポートでもラベルの周りへ寄る。
 *  - サムネイルは ShotPortraitCard と同じフォールバック規則で「一番上の
 *    メディアアイテム」を描く: shot.thumbnail → 画像は media_file を直接、
 *    動画は先頭フレームの静止表示、オーディオは生成サムネイル
 *    （GenerativeAudioThumbnail。プレビューと同じ動的カラー）、それ以外は
 *    種別アイコン。メディア未登録のショットはサムネイルなしのテキストチップ。
 *  - チップはサムネイルの実アスペクト比（事前読み込みで取得）で表示し、
 *    id ハッシュ由来のゆらぎで大きさを散らす（面積はほぼ均等）。
 *  - 表示枚数は「重ならず bounds に収まる枚数」まで試行で絞る。ビューポート
 *    が広いほど多く、狭いほど少なくなり、あふれは「他 +N件」チップへ。
 *  - チップはクリック可能。Preview への共有エレメント遷移の起点になる。
 *  - ラベルを先に読ませるため、settled から少し間を置いて表示を始める。
 *  - 納品予定日（next_delivery_date）があるショットはサムネイル下に
 *    「M/D 納品予定」を添える。
 *
 * レイヤ自体は pointer-events: none でホイール操作を妨げないが、チップは
 * pointer-events: auto でクリックを受ける。チップ上の wheel / スワイプ操作は
 * `data-wheel-gesture-surface`（RadioCardGroup の document capture リスナが
 * 拾うジェスチャ面の契約）で直接ホイール回転に届くため、カーソルや指が
 * チップに乗っていても操作が止まらない。
 */

/** 同時に表示するチップの上限。あふれは「他 +N件」チップへ。 */
const MAX_CHIPS = 8;
/** ラベルを読ませてからチップを出し始めるまでの間(s)。 */
const REVEAL_BASE_DELAY_SEC = 0.2;
/** サムネイル読み込み（実アスペクト比の確定）を待つ上限(ms)。 */
const RATIO_SETTLE_TIMEOUT_MS = 800;
/**
 * チップ基準サイズ(px)。面積の平方根（= 正方形換算の一辺）で、bounds の
 * 短辺に応じてスケールする。実際の幅・高さはアスペクト比で配分する。
 */
const BASE_SIDE_MIN_PX = 120;
const BASE_SIDE_MAX_PX = 176;
const BASE_SIDE_BOUNDS_RATIO = 0.3;
/** id ハッシュ由来のサイズゆらぎ（±15%）。 */
const SIZE_JITTER = 0.15;
/** サムネイル幅のクランプ(px)。 */
const THUMB_WIDTH_MIN_PX = 116;
const THUMB_WIDTH_MAX_PX = 280;
/** アスペクト比のクランプ（縦長 3:5 〜 横長 16:9 強）。 */
const THUMB_AR_MIN = 0.6;
const THUMB_AR_MAX = 1.9;
/** アスペクト比不明（未読込・読込失敗・動画静止フレーム）時のフォールバック。 */
const FALLBACK_THUMB_AR = 16 / 9;
/** オーディオ生成サムネイルのアスペクト比（AudioPreview と同じ正方形）。 */
const AUDIO_THUMB_AR = 1;
/** メディア未登録（サムネイルなし）チップの幅(px)。 */
const TEXT_CHIP_WIDTH_PX = 156;
/** 情報部（ショット名 1 行 + padding）の高さ見積もり(px)。 */
const CHIP_INFO_BASE_PX = 40;
/** 納品予定行ぶんの追加高さ見積もり(px)。 */
const CHIP_DATE_ROW_PX = 20;
/** 中央ラベルの回避矩形サイズ(px)。 */
const LABEL_AVOID_WIDTH_PX = 180;
const LABEL_AVOID_HEIGHT_PX = 64;
/** 「他 +N件」チップの固定サイズ。 */
const OVERFLOW_CHIP_SIZE = { width: 88, height: 30 };
const OVERFLOW_CHIP_ID = "__overflow__";

/** 生成サムネイルの静止絵用: 再生位置は常に 0 秒（安定参照）。 */
const zeroTime = () => 0;

/** チップに描くサムネイルの内容。 */
type ChipMedia =
  | { kind: "none" }
  | { kind: "image"; src: string }
  | { kind: "videoStill"; src: string }
  | { kind: "audio"; src: string }
  | { kind: "icon"; icon: string };

const NO_MEDIA: ChipMedia = { kind: "none" };

/**
 * ショットの「一番上のメディアアイテム」をチップでどう描くか決める。
 * ShotPortraitCard の cover フォールバックと同じ規則:
 *  1. メディア未登録 → サムネイルなし（テキストのみのチップ）
 *  2. shot.thumbnail（BE が返すプレビュー先頭のサムネイル）
 *  3. 画像 → media_file を直接 cover 表示
 *  4. オーディオ → 生成サムネイル。src は再生用 URL（プレビューと同じ
 *     優先順位）なので、同じ音源なら他画面と同じ配色になる
 *  5. 動画（HLS 以外）→ 先頭フレームの静止表示
 *  6. それ以外 → 種別アイコン
 */
const resolveChipMedia = (shot: Shot): ChipMedia => {
  if (!shotHasMedia(shot)) return NO_MEDIA;
  if (shot.thumbnail) return { kind: "image", src: shot.thumbnail };
  const ext = extOfUrl(shot.media_file);
  if (shot.media_file && IMAGE_EXTENSIONS.includes(ext)) {
    return { kind: "image", src: shot.media_file };
  }
  if (AUDIO_EXTENSIONS.includes(ext)) {
    const src = getShotPlaybackSrc(shot);
    if (src) return { kind: "audio", src };
  }
  if (shot.media_file && VIDEO_EXTENSIONS.includes(ext) && ext !== "m3u8") {
    return { kind: "videoStill", src: shot.media_file };
  }
  return { kind: "icon", icon: getFileIconFromExtension(shot.media_file) };
};

/** 情報部（ショット名 + あれば納品予定行）の高さ見積もり(px)。 */
const chipInfoHeight = (shot: Shot): number =>
  CHIP_INFO_BASE_PX + (shot.next_delivery_date ? CHIP_DATE_ROW_PX : 0);

/**
 * ショットごとのサムネイル表示サイズ。id ハッシュのゆらぎで面積を散らし、
 * アスペクト比 ar で幅と高さへ配分する（面積はほぼ一定に保つ）。
 */
const chipThumbSize = (
  shotId: string,
  ar: number,
  baseSide: number,
): { width: number; height: number } => {
  const jitter = 1 - SIZE_JITTER + mulberry32(hashString(shotId))() * SIZE_JITTER * 2;
  const side = baseSide * jitter;
  const clampedAr = Math.min(THUMB_AR_MAX, Math.max(THUMB_AR_MIN, ar));
  const width = Math.round(
    Math.min(
      THUMB_WIDTH_MAX_PX,
      Math.max(THUMB_WIDTH_MIN_PX, side * Math.sqrt(clampedAr)),
    ),
  );
  return { width, height: Math.round(width / clampedAr) };
};

// ===== Styled =====

const CloudLayer = styled.div`
  position: fixed;
  inset: 0;
  /* Stage(z=2) より上、パネル(z=3) と同層。レイヤ自体は操作を奪わない。 */
  z-index: 3;
  pointer-events: none;
`;

const Chip = styled(motion.button)`
  position: absolute;
  display: block;
  padding: 0;
  border-radius: 12px;
  border: 1px solid var(--soremo-border);
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  overflow: hidden;
  text-align: left;
  cursor: pointer;
  /* レイヤは none だがチップ自体はクリック / スワイプを受ける */
  pointer-events: auto;
  /* チップ上のスワイプはホイール回転として扱う（ネイティブパンに渡さない） */
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  /* iOS / iPadOS Safari は色未指定の button ラベルをアクセント色（青）で
     描くため、UA 既定に任せず明示する */
  color: #000;
`;

const ChipThumb = styled.div`
  position: relative;
  width: 100%;
  background: linear-gradient(135deg, #e8e8e8 0%, #d4d4d4 100%);
`;

const ChipThumbImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

/** サムネイル未生成の動画: 先頭フレームを静止表示（ShotPortraitCard と同じ）。 */
const ChipThumbVideoStill = styled.video`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
`;

const ChipThumbFallbackIcon = styled.span.attrs({
  className: "material-symbols-rounded",
})`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  color: var(--soremo-light-gray);
`;

const ChipInfo = styled.div`
  padding: 6px 10px 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ChipName = styled.span.attrs({ className: "bodytext-11 u-text-black" })`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const ChipDate = styled.span.attrs({
  className: "bodytext-11 u-text-light-gray",
})`
  white-space: nowrap;
`;

const OverflowChip = styled(motion.div).attrs({
  className: "bodytext-11 u-text-light-gray",
})`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: 1px solid var(--soremo-border);
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
`;

// ===== 本体 =====

export interface WaitingShotCloudProps {
  /** 待機中ショット（親が納品予定日の近い順に整列済み）。 */
  shots: Shot[];
  /** 中央カード（待機中スロット）の viewport 矩形。ラベル位置の基準。 */
  cardRect: CloudRect;
  /** チップを置いてよい viewport 領域（クローム / パネルを除いた範囲）。 */
  bounds: CloudRect;
  /** カードが中央にナッジ（settled）しているか。フェードインの起点。 */
  visible: boolean;
  /** チップクリック時。el は共有エレメント遷移の起点（サムネイル要素）。 */
  // eslint-disable-next-line no-unused-vars
  onShotClick: (shot: Shot, el: HTMLElement) => void;
  /**
   * チップをホイールのジェスチャ面として登録するキー。
   * RadioCardGroup の `gestureSurfaceKey` と同じ値を渡すと、チップ上で
   * 始まった wheel / スワイプがそのホイールの回転として扱われる。
   */
  wheelGestureKey?: string;
}

const WaitingShotCloud: React.FC<WaitingShotCloudProps> = ({
  shots,
  cardRect,
  bounds,
  visible,
  onShotClick,
  wheelGestureKey,
}) => {
  // ショットごとのサムネイル表示内容（一番上のメディアアイテム基準）。
  const chipMediaById = useMemo(
    () =>
      new Map<string, ChipMedia>(
        shots.map((shot) => [shot.id, resolveChipMedia(shot)]),
      ),
    [shots],
  );

  // ── サムネイルの実アスペクト比を事前読み込みで取得する ──
  // 対象は画像として描くチップ（thumbnail / 画像 media_file）のみ。
  // 配置は推定サイズで確定するため、表示開始（reveal）を読み込み完了まで
  // 少し待つ。上限を超えたらフォールバック比率のまま出し、後着の画像は
  // object-fit: cover のクロップで吸収する（再レイアウトしない）。
  const [thumbRatios, setThumbRatios] = useState<ReadonlyMap<string, number>>(
    () => new Map(),
  );
  const [ratiosSettled, setRatiosSettled] = useState(false);

  useEffect(() => {
    const targets = shots.slice(0, MAX_CHIPS).flatMap((shot) => {
      const media = chipMediaById.get(shot.id);
      return media?.kind === "image" ? [{ id: shot.id, src: media.src }] : [];
    });
    if (targets.length === 0) {
      setRatiosSettled(true);
      return undefined;
    }
    let cancelled = false;
    let pending = targets.length;
    setRatiosSettled(false);
    const settle = () => {
      pending -= 1;
      if (pending <= 0 && !cancelled) setRatiosSettled(true);
    };
    const images = targets.map((target) => {
      const img = new Image();
      img.onload = () => {
        if (!cancelled && img.naturalWidth > 0 && img.naturalHeight > 0) {
          const ratio = img.naturalWidth / img.naturalHeight;
          setThumbRatios((prev) => new Map(prev).set(target.id, ratio));
        }
        settle();
      };
      img.onerror = settle;
      img.src = target.src;
      return img;
    });
    const timeout = window.setTimeout(() => {
      if (!cancelled) setRatiosSettled(true);
    }, RATIO_SETTLE_TIMEOUT_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
      images.forEach((img) => {
        img.onload = null;
        img.onerror = null;
      });
    };
  }, [shots, chipMediaById]);

  // 中央のラベル（鋭意制作中 / レビュー待ち）の想定矩形。クラスタの核。
  const labelAvoidRect = useMemo<CloudRect>(() => {
    const width = Math.min(cardRect.width, LABEL_AVOID_WIDTH_PX);
    return {
      left: cardRect.left + (cardRect.width - width) / 2,
      top: cardRect.top + (cardRect.height - LABEL_AVOID_HEIGHT_PX) / 2,
      width,
      height: LABEL_AVOID_HEIGHT_PX,
    };
  }, [cardRect]);

  // 表示件数は「重ならず bounds に収まる枚数」へ動的に絞る。配置は決定的で
  // 計算も軽い（n ≤ 9）ので、件数を減らしながら試行し、置き切れた最大件数を
  // 採用する。あふれは「他 +N件」チップに集約する。
  const { visibleShots, overflowCount, placements } = useMemo(() => {
    const baseSide = Math.min(
      BASE_SIDE_MAX_PX,
      Math.max(
        BASE_SIDE_MIN_PX,
        Math.min(bounds.width, bounds.height) * BASE_SIDE_BOUNDS_RATIO,
      ),
    );
    const specFor = (shot: Shot): ClusterChipSpec => {
      const media = chipMediaById.get(shot.id) ?? NO_MEDIA;
      const infoHeight = chipInfoHeight(shot);
      if (media.kind === "none") {
        return { id: shot.id, width: TEXT_CHIP_WIDTH_PX, height: infoHeight };
      }
      const ar =
        media.kind === "audio"
          ? AUDIO_THUMB_AR
          : thumbRatios.get(shot.id) ?? FALLBACK_THUMB_AR;
      const thumb = chipThumbSize(shot.id, ar, baseSide);
      return {
        id: shot.id,
        width: thumb.width,
        height: thumb.height + infoHeight,
      };
    };

    for (let count = Math.min(MAX_CHIPS, shots.length); count >= 1; count -= 1) {
      const chosen = shots.slice(0, count);
      const specs: ClusterChipSpec[] = chosen.map(specFor);
      if (shots.length > count) {
        specs.push({ id: OVERFLOW_CHIP_ID, ...OVERFLOW_CHIP_SIZE });
      }
      const placed = layoutLabelClusterChips(specs, labelAvoidRect, bounds);
      if (placed) {
        return {
          visibleShots: chosen,
          overflowCount: shots.length - count,
          placements: placed,
        };
      }
    }
    return {
      visibleShots: [] as Shot[],
      overflowCount: shots.length,
      placements: [],
    };
  }, [shots, bounds, labelAvoidRect, thumbRatios, chipMediaById]);

  const placementById = useMemo(
    () => new Map(placements.map((p) => [p.id, p])),
    [placements],
  );

  // チップは pointer-events: auto でクリックを受けるが、wheel / スワイプは
  // ジェスチャ面属性（下記 gestureSurfaceProps）経由でホイール回転に届く。
  const gestureSurfaceProps = wheelGestureKey
    ? { [WHEEL_GESTURE_SURFACE_ATTR]: wheelGestureKey }
    : undefined;

  if (!visible || !ratiosSettled || visibleShots.length === 0) {
    // AnimatePresence の exit を効かせるため、レイヤ自体は残して空にする。
    return createPortal(
      <CloudLayer aria-hidden="true">
        <AnimatePresence />
      </CloudLayer>,
      document.body,
    );
  }

  const overflowPlacement =
    overflowCount > 0 ? placementById.get(OVERFLOW_CHIP_ID) : undefined;

  return createPortal(
    <CloudLayer>
      <AnimatePresence>
        {visibleShots.map((shot, i) => {
          const placement = placementById.get(shot.id);
          if (!placement) return null;
          const media = chipMediaById.get(shot.id) ?? NO_MEDIA;
          const thumbHeight = placement.height - chipInfoHeight(shot);
          return (
            <Chip
              key={shot.id}
              type="button"
              aria-label={shot.shot_name}
              style={{
                left: placement.left,
                top: placement.top,
                width: placement.width,
              }}
              initial={{ opacity: 0, y: 10, scale: 0.96 }}
              animate={{
                opacity: 1,
                y: 0,
                scale: 1,
                transition: {
                  delay: REVEAL_BASE_DELAY_SEC + i * CLOUD_STAGGER_SEC,
                  duration: CLOUD_BUBBLE_FADE_SEC,
                  ease: "easeOut",
                },
              }}
              exit={{ opacity: 0, transition: { duration: 0.15 } }}
              {...gestureSurfaceProps}
              onClick={(e) => {
                e.stopPropagation();
                const thumbEl = e.currentTarget.querySelector<HTMLElement>(
                  `[${WHEEL_SHOT_MEDIA_ATTR}="${shot.id}"]`,
                );
                onShotClick(shot, thumbEl ?? e.currentTarget);
              }}
            >
              {media.kind !== "none" && (
                <ChipThumb
                  style={{ height: thumbHeight }}
                  {...{ [WHEEL_SHOT_MEDIA_ATTR]: shot.id }}
                >
                  {media.kind === "image" && (
                    <ChipThumbImage src={media.src} alt="" loading="lazy" />
                  )}
                  {media.kind === "videoStill" && (
                    <ChipThumbVideoStill
                      src={`${media.src}#t=0.1`}
                      muted
                      playsInline
                      preload="metadata"
                      aria-hidden="true"
                      tabIndex={-1}
                    />
                  )}
                  {media.kind === "audio" && (
                    <GenerativeAudioThumbnail
                      src={media.src}
                      isPlaying={false}
                      glowTrigger="none"
                      getCurrentTime={zeroTime}
                    />
                  )}
                  {media.kind === "icon" && (
                    <ChipThumbFallbackIcon aria-hidden="true">
                      {media.icon}
                    </ChipThumbFallbackIcon>
                  )}
                </ChipThumb>
              )}
              <ChipInfo>
                <ChipName translate="no">{shot.shot_name}</ChipName>
                {shot.next_delivery_date && (
                  <ChipDate>
                    <DateDisplay
                      date={shot.next_delivery_date}
                      type={DATE_DISPLAY_TYPE.DATE_ONLY}
                    />{" "}
                    納品予定
                  </ChipDate>
                )}
              </ChipInfo>
            </Chip>
          );
        })}
        {overflowPlacement && (
          <OverflowChip
            key={OVERFLOW_CHIP_ID}
            style={{
              left: overflowPlacement.left,
              top: overflowPlacement.top,
              width: overflowPlacement.width,
              height: overflowPlacement.height,
            }}
            initial={{ opacity: 0, y: 10 }}
            animate={{
              opacity: 1,
              y: 0,
              transition: {
                delay:
                  REVEAL_BASE_DELAY_SEC +
                  visibleShots.length * CLOUD_STAGGER_SEC,
                duration: CLOUD_BUBBLE_FADE_SEC,
                ease: "easeOut",
              },
            }}
            exit={{ opacity: 0, transition: { duration: 0.15 } }}
          >
            他 +{overflowCount}件
          </OverflowChip>
        )}
      </AnimatePresence>
    </CloudLayer>,
    document.body,
  );
};

export default WaitingShotCloud;
